/**
 * ChatPage.jsx — Main Chat Interface
 *
 * Features added (Layer 1 gap-analysis):
 *   L1-4  Offline banner — detects network loss, blocks sending while offline
 *   L1-2  SESSION_EXPIRED handling — auto-logout when API returns 401
 *   L1-5  Input sanitisation — blocks prompt-injection before send
 *   L1-6  Conversation search — filter visible messages by keyword
 *   L1-8  Conversation export — download full chat as a text file / print to PDF
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, Mic, MicOff, AlertCircle,
  Package, ShoppingCart, DollarSign, Search,
  Sun, Moon, WifiOff, X, Download, FileText, Flag,
} from 'lucide-react';
import { useAuth }         from '../context/AuthContext';
import { useTheme }        from '../context/ThemeContext';
import { sendMessage, getConversation } from '../utils/api';
import { useVoiceInput }   from '../hooks/useVoiceInput';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { sanitizeInput }   from '../utils/sanitize';
import Sidebar             from '../components/Sidebar';
import BrandLogo           from '../components/BrandLogo';
import ChatMessage         from '../components/ChatMessage';
import TypingIndicator     from '../components/TypingIndicator';
import ReportIssueModal    from '../components/ReportIssueModal';
import logger              from '../utils/logger';
import './ChatPage.css';

// ── Suggested starter queries shown on welcome screen ─────────────────────────
const SUGGESTED_QUERIES = [
  { icon: ShoppingCart, label: 'Open Sales Orders',        query: 'Show me open sales orders',                    module: 'SD' },
  { icon: Package,      label: 'Purchase Orders',          query: 'List purchase orders created this week',       module: 'MM' },
  { icon: DollarSign,   label: 'Pending Invoices',         query: 'Which supplier invoices are pending payment?', module: 'FI' },
  { icon: Search,       label: 'Stock Levels',             query: 'Show stock levels for plant 1010',             module: 'MM' },
  { icon: Package,      label: 'Production Orders',        query: 'List open production orders',                  module: 'PP' },
  { icon: DollarSign,   label: 'Overdue Invoices',         query: 'Show overdue customer invoices',               module: 'FI' },
];

// ── Module colour map ─────────────────────────────────────────────────────────
const MODULE_COLOURS = {
  MM: 'var(--clr-mm)', SD: 'var(--clr-sd)',
  FI: 'var(--clr-fi)', PP: 'var(--clr-pp)',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ChatPage() {
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme }  = useTheme();
  const isOnline                = useOnlineStatus(); // L1-4

  // ── State ─────────────────────────────────────────────────────────────────
  const [messages,        setMessages]        = useState([]);
  const [inputValue,      setInputValue]      = useState('');
  const [isLoading,       setIsLoading]       = useState(false);
  const [voiceError,      setVoiceError]      = useState(null);
  const [convId,          setConvId]          = useState(() => `conv_${Date.now()}`);
  const [sidebarRefresh,  setSidebarRefresh]  = useState(0);

  // L1-5: sanitisation error shown in the input area
  const [sanitizeError,   setSanitizeError]   = useState(null);

  // L1-6: conversation-level search
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchVisible,   setSearchVisible]   = useState(false);
  const searchInputRef = useRef(null);

  // Report issue modal
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const textareaRef    = useRef(null);

  // ── Voice input hook ──────────────────────────────────────────────────────
  const handleVoiceResult = useCallback((transcript) => {
    logger.info('ChatPage: Voice transcript received', { transcript });
    setInputValue((prev) => prev ? `${prev} ${transcript}` : transcript);
    inputRef.current?.focus();
  }, []);

  const {
    isListening, isSupported: voiceSupported, error: recognitionError,
    interimText, toggleListening,
  } = useVoiceInput(handleVoiceResult);

  // ── Auto-scroll to latest message ─────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // ── Auto-resize textarea ──────────────────────────────────────────────────
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [inputValue]);

  // ── Show voice error in UI ────────────────────────────────────────────────
  useEffect(() => {
    if (recognitionError) {
      setVoiceError(recognitionError);
      const timer = setTimeout(() => setVoiceError(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [recognitionError]);

  // ── Focus search input when search bar opens ──────────────────────────────
  useEffect(() => {
    if (searchVisible) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchVisible]);

  // ── Generate unique message ID ─────────────────────────────────────────────
  const genId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  // ── L1-6: Filter messages by search query ─────────────────────────────────
  // Searches across text, summary, and all table cell values
  const visibleMessages = searchQuery.trim()
    ? messages.filter((msg) => {
        const q = searchQuery.toLowerCase();
        if (msg.text?.toLowerCase().includes(q))    return true;
        if (msg.summary?.toLowerCase().includes(q)) return true;
        if (msg.tableData?.headers?.some((h) => h.toLowerCase().includes(q))) return true;
        if (msg.tableData?.rows?.some((row) =>
          row.some((cell) => String(cell ?? '').toLowerCase().includes(q))
        )) return true;
        return false;
      })
    : messages;

  // ── L1-8: Export conversation as plain text ────────────────────────────────
  const handleExportConversation = useCallback(() => {
    if (messages.length === 0) return;

    const lines = [];
    const now   = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    const timeStr = now.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });

    lines.push(`Querely — Conversation Export`);
    lines.push(`Exported: ${dateStr} ${timeStr}`);
    lines.push(`User: ${user?.name || user?.email || 'Unknown'}`);
    lines.push(`Tenant: ${user?.tenant || 'Unknown'}`);
    lines.push('='.repeat(60));
    lines.push('');

    messages.forEach((msg) => {
      const role = msg.role === 'user' ? 'YOU' : 'QUERELY';
      const ts   = msg.timestamp
        ? new Date(msg.timestamp).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })
        : '';
      lines.push(`[${ts}] ${role}`);

      if (msg.type === 'table') {
        if (msg.summary) lines.push(msg.summary.replace(/\*\*/g, ''));
        if (msg.tableData) {
          // Render table as fixed-width text
          const { headers, rows } = msg.tableData;
          const allRows  = [headers, ...rows];
          const colWidths = headers.map((_, ci) =>
            Math.max(...allRows.map((row) => String(row[ci] ?? '').length))
          );
          const separator = colWidths.map((w) => '-'.repeat(w + 2)).join('+');
          const formatRow = (row) =>
            row.map((cell, ci) => ` ${String(cell ?? '').padEnd(colWidths[ci])} `).join('|');

          lines.push(separator);
          lines.push(formatRow(headers));
          lines.push(separator);
          rows.forEach((row) => lines.push(formatRow(row)));
          lines.push(separator);
        }
      } else {
        // Remove simple markdown markers for plain text
        lines.push((msg.text || '').replace(/\*\*/g, '').replace(/\*/g, ''));
      }

      lines.push('');
    });

    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/plain;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const d = new Date();
    a.href     = url;
    a.download = `Querely_Chat_${String(d.getDate()).padStart(2,'0')}${months[d.getMonth()]}${d.getFullYear()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    logger.info('ChatPage: Conversation exported as text');
  }, [messages, user]);

  // ── L1-8: Print conversation (user can Save as PDF from print dialog) ─────
  const handlePrintConversation = useCallback(() => {
    window.print();
  }, []);

  // ── Send message handler ──────────────────────────────────────────────────
  const handleSend = useCallback(async (queryText) => {
    const raw = (queryText || inputValue).trim();
    if (!raw || isLoading) return;

    // L1-4: Block send when offline
    if (!isOnline) {
      setSanitizeError('You are offline. Please check your network connection.');
      return;
    }

    // L1-5: Sanitise input before sending
    const { clean, blocked, reason } = sanitizeInput(raw);
    if (blocked) {
      setSanitizeError(reason);
      return;
    }
    setSanitizeError(null);

    logger.info('ChatPage: Sending message', { text: clean });

    const userMsg = {
      id:        genId(),
      role:      'user',
      type:      'text',
      text:      clean,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await sendMessage(clean, convId, token);

      const aiMsg = {
        id:          genId(),
        role:        'assistant',
        type:        response.type   || 'text',
        text:        response.text,
        summary:     response.summary,
        tableData:   response.tableData,
        module:      response.module,
        suggestions: response.suggestions || [],
        timestamp:   new Date(),
      };

      setMessages((prev) => [...prev, aiMsg]);
      logger.info('ChatPage: AI response displayed', { module: response.module });
      setSidebarRefresh((n) => n + 1);

    } catch (err) {
      logger.error('ChatPage: Send failed', err);

      // L1-2: Token expired → force logout instead of showing confusing error
      if (err.code === 'SESSION_EXPIRED') {
        logger.warn('ChatPage: Session expired — logging out');
        await logout();
        return;
      }

      const errMsg = {
        id:        genId(),
        role:      'assistant',
        type:      'error',
        text:      err.message || 'Something went wrong. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => {
        const updated = [...prev, errMsg];
        // Persist frontend errors to localStorage — backend never saves these
        try {
          const key = `cai_errors_${convId}`;
          const saved = JSON.parse(localStorage.getItem(key) || '[]');
          localStorage.setItem(key, JSON.stringify([...saved, { ...errMsg, timestamp: errMsg.timestamp.toISOString() }]));
        } catch (_) {}
        return updated;
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [inputValue, isLoading, convId, token, isOnline, logout]);

  // ── Keyboard handler: Enter sends, Shift+Enter = new line ─────────────────
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Close search on Escape
    if (e.key === 'Escape' && searchVisible) {
      setSearchVisible(false);
      setSearchQuery('');
    }
  }, [handleSend, searchVisible]);

  // ── New conversation ───────────────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    setMessages([]);
    setConvId(`conv_${Date.now()}`);
    setInputValue('');
    setSearchQuery('');
    setSearchVisible(false);
    logger.info('ChatPage: New conversation started');
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // ── Load a past conversation from sidebar ──────────────────────────────────
  const handleLoadConversation = useCallback(async (conversationId) => {
    if (conversationId === convId) return;
    logger.info('ChatPage: Loading conversation', { conversationId });
    setSearchQuery('');
    setSearchVisible(false);
    try {
      const data = await getConversation(token, conversationId);
      const loadedMessages = data.messages.map((m) => {
        if (m.role === 'user') {
          return { id: genId(), role: 'user', type: 'text', text: m.content, timestamp: new Date(m.created_at) };
        }
        try {
          const parsed = JSON.parse(m.content);
          return {
            id:        genId(),
            role:      'assistant',
            type:      parsed.type   || 'text',
            text:      parsed.text,
            summary:   parsed.summary,
            tableData: parsed.table_data,
            module:    parsed.module,
            timestamp: new Date(m.created_at),
          };
        } catch {
          return { id: genId(), role: 'assistant', type: 'text', text: m.content, timestamp: new Date(m.created_at) };
        }
      });
      // Re-inject frontend-only error messages (network errors never saved to backend)
      const savedErrors = (() => {
        try { return JSON.parse(localStorage.getItem(`cai_errors_${conversationId}`) || '[]'); } catch { return []; }
      })().map(e => ({ ...e, timestamp: new Date(e.timestamp) }));

      // Merge errors into the message list by timestamp order
      const merged = [...loadedMessages, ...savedErrors].sort((a, b) => a.timestamp - b.timestamp);
      setMessages(merged);
      setConvId(conversationId);
      setInputValue('');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      logger.error('ChatPage: Failed to load conversation', err);
    }
  }, [token, convId]);

  // ── Module chip click from sidebar → prefill query ─────────────────────────
  const handleModuleQuery = useCallback((query) => {
    setInputValue(query);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="chat-page">
      {/* Sidebar */}
      <Sidebar
        onNewChat={handleNewChat}
        onModuleClick={handleModuleQuery}
        activeChat={convId}
        onLoadConversation={handleLoadConversation}
        refreshKey={sidebarRefresh}
      />

      {/* ── Main content area ── */}
      <main className="chat-main">

        {/* ── Top bar ── */}
        <header className="chat-header">
          <div className="chat-header-left">
            {isOnline ? (
              <span className="chat-header-status">
                <span className="chat-status-dot" />
                Live
              </span>
            ) : (
              <span className="chat-header-status chat-header-status--offline">
                <WifiOff size={11} />
                Offline
              </span>
            )}
          </div>
          <div className="chat-header-right">
            <span className="chat-header-tenant">{user?.tenant}</span>
            <div className="chat-header-modules">
              {user?.modules?.map((m) => (
                <span
                  key={m}
                  className="chat-header-module"
                  style={{ color: MODULE_COLOURS[m] || 'var(--clr-accent)' }}
                >
                  {m}
                </span>
              ))}
            </div>

            {/* L1-6: Search toggle */}
            {messages.length > 0 && (
              <button
                className={`chat-icon-btn ${searchVisible ? 'chat-icon-btn--active' : ''}`}
                onClick={() => {
                  setSearchVisible((v) => !v);
                  if (searchVisible) setSearchQuery('');
                }}
                aria-label="Search conversation"
                title="Search conversation (Ctrl+F)"
              >
                <Search size={15} />
              </button>
            )}

            {/* L1-8: Export conversation */}
            {messages.length > 0 && (
              <div className="chat-export-menu">
                <button
                  className="chat-icon-btn"
                  onClick={handleExportConversation}
                  aria-label="Export conversation as text"
                  title="Download conversation as .txt"
                >
                  <Download size={15} />
                </button>
                <button
                  className="chat-icon-btn"
                  onClick={handlePrintConversation}
                  aria-label="Print / Save as PDF"
                  title="Print or Save as PDF"
                >
                  <FileText size={15} />
                </button>
              </div>
            )}

            {/* Report Issue */}
            <button
              className="chat-icon-btn"
              onClick={() => setReportModalOpen(true)}
              aria-label="Report an issue"
              title="Report an issue"
            >
              <Flag size={15} />
            </button>

            {/* Theme toggle button */}
            <button
              className="chat-theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </header>

        {/* L1-4: Offline banner — appears below header when network is lost */}
        {!isOnline && (
          <div className="chat-offline-banner" role="alert">
            <WifiOff size={14} />
            <span>
              <strong>You're offline.</strong> Check your network connection.
              Messages cannot be sent until connectivity is restored.
            </span>
          </div>
        )}

        {/* L1-6: Conversation search bar — slides in when searchVisible */}
        {searchVisible && (
          <div className="chat-search-bar">
            <Search size={14} className="chat-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="chat-search-input"
              placeholder="Search this conversation…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search messages"
            />
            {searchQuery && (
              <span className="chat-search-count">
                {visibleMessages.length} result{visibleMessages.length !== 1 ? 's' : ''}
              </span>
            )}
            <button
              className="chat-search-close"
              onClick={() => { setSearchVisible(false); setSearchQuery(''); }}
              aria-label="Close search"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Messages area ── */}
        <section className="chat-messages" aria-label="Conversation" aria-live="polite">

          {/* Welcome screen */}
          {messages.length === 0 && !isLoading && (
            <WelcomeScreen
              user={user}
              onSuggestion={(query) => {
                setInputValue(query);
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
            />
          )}

          {/* L1-6: Show "no results" state when search returns nothing */}
          {searchQuery && visibleMessages.length === 0 && messages.length > 0 && (
            <div className="chat-search-empty">
              <Search size={20} />
              <span>No messages match "<strong>{searchQuery}</strong>"</span>
            </div>
          )}

          {/* Message list — filtered when search is active */}
          {visibleMessages.map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              onSuggestionClick={(q) => {
                setInputValue(q);
                setTimeout(() => handleSend(q), 50);
              }}
            />
          ))}

          {/* Typing indicator — only shown when not searching */}
          {isLoading && !searchQuery && <TypingIndicator />}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </section>

        {/* ── Voice error banner ── */}
        {voiceError && (
          <div className="chat-voice-error" role="alert">
            <AlertCircle size={14} />
            <span>{voiceError}</span>
          </div>
        )}

        {/* ── Interim voice text ── */}
        {interimText && (
          <div className="chat-interim-text" aria-live="polite">
            <Mic size={12} />
            <span>{interimText}…</span>
          </div>
        )}

        {/* L1-5: Sanitisation / validation error banner */}
        {sanitizeError && (
          <div className="chat-voice-error" role="alert">
            <AlertCircle size={14} />
            <span>{sanitizeError}</span>
            <button
              className="chat-error-dismiss"
              onClick={() => setSanitizeError(null)}
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* ── Input bar ── */}
        <div className="chat-input-bar">
          <div className={`chat-input-wrapper ${isListening ? 'chat-input-wrapper--recording' : ''} ${!isOnline ? 'chat-input-wrapper--disabled' : ''}`}>

            {/* Textarea */}
            <textarea
              ref={(el) => { textareaRef.current = el; inputRef.current = el; }}
              className="chat-textarea"
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setSanitizeError(null); }}
              onKeyDown={handleKeyDown}
              placeholder={
                !isOnline       ? 'Offline — cannot send messages'           :
                isListening     ? 'Listening… speak your SAP query'          :
                                  'Ask anything about your SAP data…'
              }
              rows={1}
              disabled={isLoading || !isOnline}
              aria-label="Chat input"
              aria-multiline="true"
            />

            {/* Voice button */}
            {voiceSupported && (
              <button
                className={`chat-voice-btn ${isListening ? 'chat-voice-btn--active' : ''}`}
                onClick={toggleListening}
                disabled={isLoading || !isOnline}
                aria-label={isListening ? 'Stop recording' : 'Start voice input'}
                title={isListening ? 'Stop recording' : 'Speak your query'}
              >
                {isListening ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
            )}

            {/* Send button */}
            <button
              className="chat-send-btn"
              onClick={() => handleSend()}
              disabled={!inputValue.trim() || isLoading || !isOnline}
              aria-label="Send message"
              title="Send (Enter)"
            >
              <Send size={18} />
            </button>
          </div>

          <p className="chat-input-hint">
            Press <kbd>Enter</kbd> to send &nbsp;·&nbsp; <kbd>Shift+Enter</kbd> for new line
            {voiceSupported && <> &nbsp;·&nbsp; Click <Mic size={10} style={{display:'inline'}}/> to speak</>}
          </p>
        </div>
      </main>

      {/* Report Issue Modal */}
      {reportModalOpen && (
        <ReportIssueModal
          conversationId={convId}
          onClose={() => setReportModalOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Welcome Screen ───────────────────────────────────────────────────────────

function WelcomeScreen({ user, onSuggestion }) {
  return (
    <div className="welcome">
      <div className="welcome-inner">
        <div className="welcome-greeting">
          <div className="welcome-icon" aria-hidden="true">
            <BrandLogo size={56} />
          </div>
          <h1 className="welcome-title">
            Hello, {user?.name?.split(' ')[0] || 'there'} 👋
          </h1>
          <p className="welcome-subtitle">
            I'm Querely — your AI assistant connected to SAP.<br />
            Ask me anything about your business data in plain English.
          </p>
        </div>

        <div className="welcome-suggestions">
          <p className="welcome-suggestions-label">Try asking:</p>
          <div className="welcome-cards">
            {SUGGESTED_QUERIES.filter(({ module }) =>
              !user?.modules?.length || user.modules.includes(module)
            ).map(({ icon: Icon, label, query, module }) => (
              <button
                key={query}
                className="welcome-card"
                onClick={() => onSuggestion(query)}
              >
                <div className="welcome-card-icon"
                  style={{ color: MODULE_COLOURS[module] || 'var(--clr-accent)' }}>
                  <Icon size={20} />
                </div>
                <div className="welcome-card-content">
                  <div className="welcome-card-label">{label}</div>
                  <div className="welcome-card-query">"{query}"</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
